class VaseBundleManager extends HTMLElement {
  constructor() {
    super();
    this.selectedBundle = null;
    this.originalMediaId = null;
    this.productMedia = null;
    this.productForm = null;
    this.flickityCarousel = null;
    this.basePriceCents = null;
  }

  // Cart AJAX API exposes parent_key; Liquid exposes parent.key on line items.
  getNestedParentKey(parentRelationship) {
    if (!parentRelationship) {
      return undefined;
    }
    return parentRelationship.parent_key || parentRelationship.parent?.key;
  }

  showCartNotification(detail) {
    document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail
    }));
  }

  connectedCallback() {
    this.productMedia = document.querySelector('product-media');
    this.productForm = document.querySelector('form[is="product-form"]');
    this.flickityCarousel = this.productMedia?.querySelector('flickity-carousel');
    
    this.originalMediaId = this.getCurrentMediaId();
    this.basePriceCents = this.getDisplayedPriceCents();
    
    const vaseCarousel = this.querySelector('flickity-carousel');
    if (vaseCarousel) {
      this.setupCarouselEvents();
    }
    
    this.patchZoomFunctionality();
  }

  patchZoomFunctionality() {
    const zoomElement = this.productMedia?.querySelector('product-image-zoom');
    if (!zoomElement || zoomElement._buildItemsPatched) {
      return;
    }

    const originalBuildItems = zoomElement._buildItems.bind(zoomElement);
    zoomElement._buildItems = async function() {
      const items = await originalBuildItems();
      
      const selectedMediaItem = this.mediaElement?.querySelector('.product__media-item.is-selected');
      if (selectedMediaItem) {
        const bundleImageUrl = selectedMediaItem.getAttribute('data-bundle-image-url');
        const bundleThumbnailUrl = selectedMediaItem.getAttribute('data-bundle-thumbnail-url');
        
        if (bundleImageUrl && bundleThumbnailUrl) {
          const selectedIndex = items.findIndex(item => item.selected);
          if (selectedIndex >= 0) {
            items[selectedIndex].src = bundleImageUrl;
            items[selectedIndex].msrc = bundleThumbnailUrl;
            const bundleImg = selectedMediaItem.querySelector('[data-bundle-image] img');
            if (bundleImg && bundleImg.naturalWidth && bundleImg.naturalHeight) {
              const aspectRatio = bundleImg.naturalWidth / bundleImg.naturalHeight;
              items[selectedIndex].h = Math.round(items[selectedIndex].w / aspectRatio);
            }
          }
        }
      }
      
      return items;
    };
    
    zoomElement._buildItemsPatched = true;
  }

  setupCarouselEvents() {
    const cards = this.querySelectorAll('.boa-vase-card');
    cards.forEach(card => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const cell = card.closest('.boa-vase-carousel__cell');
        if (cell) {
          this.handleVaseSelection(cell);
        }
      });
    });
  }

  getCurrentMediaId() {
    if (!this.flickityCarousel) {
      return null;
    }
    const selectedSlide = this.flickityCarousel.querySelector('.is-selected');
    return selectedSlide ? selectedSlide.getAttribute('data-media-id') : null;
  }

  getDisplayedPriceCents() {
    const priceEl = document.querySelector('[data-product-price] .price');
    if (!priceEl) {
      return null;
    }
    const digits = (priceEl.textContent || '').replace(/[^0-9]/g, '');
    return digits ? parseInt(digits, 10) : null;
  }

  updateDisplayedPrice(totalCents) {
    const priceEl = document.querySelector('[data-product-price] .price');
    if (!priceEl) {
      return;
    }
    if (!priceEl.hasAttribute('data-original-price-text')) {
      priceEl.setAttribute('data-original-price-text', priceEl.textContent || '');
    }
    priceEl.textContent = typeof formatMoney === 'function' ? formatMoney(totalCents) : (totalCents / 100).toFixed(2);
  }

  restoreDisplayedPrice() {
    const priceEl = document.querySelector('[data-product-price] .price');
    if (!priceEl) {
      return;
    }
    const original = priceEl.getAttribute('data-original-price-text');
    if (original != null) {
      priceEl.textContent = original;
      priceEl.removeAttribute('data-original-price-text');
    }
  }

  handleVaseSelection(vaseCell) {
    const card = vaseCell.querySelector('.boa-vase-card');
    
    if (card && card.classList.contains('boa-vase-card--selected')) {
      this.resetSelection();
      return;
    }
    
    this.clearSelection();

    const vaseProductTagsJson = vaseCell.dataset.vaseProductTags || '[]';
    let vaseProductTags = [];
    try {
      vaseProductTags = JSON.parse(vaseProductTagsJson);
    } catch (error) {
      vaseProductTags = [];
    }

    const bundleData = {
      vaseId: vaseCell.dataset.bundleVaseId,
      flowerId: vaseCell.dataset.bundleFlowerId,
      imageUrl: vaseCell.dataset.bundleImageUrl,
      imageFullUrl: vaseCell.dataset.bundleImageFullUrl,
      vaseTitle: vaseCell.dataset.vaseTitle,
      vasePriceCents: Number(vaseCell.dataset.bundleVasePrice || 0),
      vaseProductTags: vaseProductTags,
      vaseReserveDays: Number(vaseCell.dataset.vaseReserveDays) || 0,
      vaseVariantTitle: vaseCell.dataset.vaseVariantTitle || ''
    };
    bundleData.bundleKey = `${bundleData.flowerId}:${bundleData.vaseId}`;

    this.selectedBundle = bundleData;

    if (card) {
      card.classList.add('boa-vase-card--selected');
      this.updateCardBadgeText(card, true);
    }
    this.updateAddToCartButton();
    this.switchToBundleImage(bundleData.imageUrl);
    this.updateZoomTargets(bundleData.imageFullUrl || bundleData.imageUrl);

    if (this.basePriceCents == null) {
      this.basePriceCents = this.getDisplayedPriceCents() || 0;
    }
    const total = Number(this.basePriceCents || 0) + (this.selectedBundle?.vasePriceCents || 0);
    this.updateDisplayedPrice(total);
  }

  clearSelection() {
    const selectedCard = this.querySelector('.boa-vase-card--selected');
    if (selectedCard) {
      selectedCard.classList.remove('boa-vase-card--selected');
      this.updateCardBadgeText(selectedCard, false);
    }
  }

  updateCardBadgeText(card, selected) {
    const badgeText = card.querySelector('.boa-vase-card__badge-text');
    if (!badgeText) {
      return;
    }
    const key = selected ? 'data-badge-selected' : 'data-badge-default';
    const fallback = selected ? 'Selected' : 'Select';
    badgeText.textContent = this.getAttribute(key) || fallback;
  }

  async resetSelection() {
    this.clearSelection();
    this.selectedBundle = null;
    this.restoreOriginalImage();
    this.updateAddToCartButton();
    this.restoreDisplayedPrice();
    this.restoreZoomTargets();
  }

  switchToBundleImage(imageUrl) {
    const currentMediaItem = this.flickityCarousel?.querySelector('.product__media-item.is-selected');
    if (!currentMediaItem) {
      return;
    }

    const currentImg = currentMediaItem.querySelector('img:not(.is-bundle-image)');
    if (!currentImg) {
      return;
    }

    if (!currentImg.getAttribute('data-original-src')) {
      currentImg.setAttribute('data-original-src', currentImg.getAttribute('src') || currentImg.src || '');
    }

    const normalizeUrl = (url) => {
      if (!url) {
        return url;
      }
      try {
        const urlObj = new URL(url, window.location.href);
        return urlObj.href;
      } catch {
        return url;
      }
    };

    const absoluteImageUrl = normalizeUrl(imageUrl);
    const fullUrl = normalizeUrl((this.selectedBundle && (this.selectedBundle.imageFullUrl || this.selectedBundle.imageUrl)) || absoluteImageUrl);

    currentImg.setAttribute('src', absoluteImageUrl);
    currentImg.src = absoluteImageUrl;
    currentImg.setAttribute('srcset', '');
    currentImg.setAttribute('sizes', '');
    currentImg.setAttribute('alt', `${this.selectedBundle?.vaseTitle || 'Bundle'} Bundle`);
    currentImg.setAttribute('loading', 'eager');

    const anchor = currentImg.closest('a');
    if (anchor) {
      if (!anchor.getAttribute('data-original-href')) {
        anchor.setAttribute('data-original-href', anchor.getAttribute('href') || anchor.href || '');
      }
      anchor.setAttribute('href', fullUrl);
      anchor.href = fullUrl;
      anchor.setAttribute('data-pswp-src', fullUrl);
      anchor.setAttribute('data-image', fullUrl);
    }

    currentMediaItem.setAttribute('data-bundle-image-url', fullUrl);
    currentMediaItem.setAttribute('data-bundle-thumbnail-url', absoluteImageUrl);
  }

  updateZoomTargets(fullUrl) {
    const zoomButtons = document.querySelectorAll('.product__zoom-button');
    zoomButtons.forEach(btn => {
      if (!btn.getAttribute('data-original-pswp-src')) {
        const existing = btn.getAttribute('data-pswp-src') || btn.getAttribute('data-image') || '';
        btn.setAttribute('data-original-pswp-src', existing);
      }
      btn.setAttribute('data-pswp-src', fullUrl);
      btn.setAttribute('data-image', fullUrl);
    });
  }

  restoreZoomTargets() {
    const zoomButtons = document.querySelectorAll('.product__zoom-button');
    zoomButtons.forEach(btn => {
      const original = btn.getAttribute('data-original-pswp-src');
      if (original != null) {
        if (original) {
          btn.setAttribute('data-pswp-src', original);
          btn.setAttribute('data-image', original);
        } else {
          btn.removeAttribute('data-pswp-src');
          btn.removeAttribute('data-image');
        }
        btn.removeAttribute('data-original-pswp-src');
      }
    });
  }

  restoreOriginalImage() {
    const currentMediaItem = this.flickityCarousel?.querySelector('.product__media-item.is-selected');
    if (!currentMediaItem) {
      return;
    }

    const currentImg = currentMediaItem.querySelector('img:not(.is-bundle-image)');
    if (!currentImg) {
      currentMediaItem.removeAttribute('data-bundle-image-url');
      currentMediaItem.removeAttribute('data-bundle-thumbnail-url');
      return;
    }

    if (currentImg.hasAttribute('data-original-src')) {
      const originalSrc = currentImg.getAttribute('data-original-src');
      if (originalSrc) {
        currentImg.setAttribute('src', originalSrc);
        currentImg.src = originalSrc;
        currentImg.removeAttribute('data-original-src');
      }
    }

    const anchor = currentImg.closest('a');
    if (!anchor || !anchor.hasAttribute('data-original-href')) {
      currentMediaItem.removeAttribute('data-bundle-image-url');
      currentMediaItem.removeAttribute('data-bundle-thumbnail-url');
      return;
    }

    const originalHref = anchor.getAttribute('data-original-href');
    if (originalHref) {
      anchor.setAttribute('href', originalHref);
      anchor.href = originalHref;
    }
    anchor.removeAttribute('data-pswp-src');
    anchor.removeAttribute('data-image');
    anchor.removeAttribute('data-original-href');
    
    currentMediaItem.removeAttribute('data-bundle-image-url');
    currentMediaItem.removeAttribute('data-bundle-thumbnail-url');
  }

  getAddToCartButtons() {
    if (!this.productForm) {
      return [];
    }

    // Use getAttribute — form.id returns the variant input named "id", not the form's id.
    const formId = this.productForm.getAttribute('id');
    let buttons = [];

    if (formId) {
      buttons = Array.from(document.querySelectorAll(
        `[data-product-add-to-cart-button][form="${formId}"]`
      ));
    }

    if (!buttons.length) {
      buttons = Array.from(this.productForm.querySelectorAll('[data-product-add-to-cart-button]'));
    }

    return buttons;
  }

  updateAddToCartButton() {
    const addToCartButtons = this.getAddToCartButtons();
    if (!addToCartButtons.length) {
      return;
    }

    addToCartButtons.forEach((addToCartButton) => {
      if (this.selectedBundle) {
        if (!addToCartButton.hasAttribute('data-original-text')) {
          const label = addToCartButton.querySelector('.loader-button__text');
          const text = label ? label.textContent.trim() : addToCartButton.textContent.trim();
          addToCartButton.setAttribute('data-original-text', text);
        }
        if (!addToCartButton.hasAttribute('data-original-display')) {
          addToCartButton.setAttribute('data-original-display', addToCartButton.style.display || '');
        }
        const bundleText = this.getBundleButtonText();
        const label = addToCartButton.querySelector('.loader-button__text');
        if (label) {
          label.textContent = bundleText;
        } else {
          addToCartButton.textContent = bundleText;
        }
        addToCartButton.classList.add('button--bundle');
        addToCartButton.style.display = '';
        return;
      }

      const originalText = addToCartButton.getAttribute('data-original-text');
      if (originalText) {
        const label = addToCartButton.querySelector('.loader-button__text');
        if (label) {
          label.textContent = originalText;
        } else {
          addToCartButton.textContent = originalText;
        }
        addToCartButton.removeAttribute('data-original-text');
      }
      const originalDisplay = addToCartButton.getAttribute('data-original-display');
      if (originalDisplay !== null) {
        addToCartButton.style.display = originalDisplay === '' ? '' : originalDisplay;
        addToCartButton.removeAttribute('data-original-display');
      }
      addToCartButton.classList.remove('button--bundle');
      addToCartButton.classList.remove('is-loading');
      addToCartButton.disabled = false;
    });
  }

  getBundleButtonText() {
    const vaseName = this.selectedBundle ? this.selectedBundle.vaseTitle : '';
    const template = window.themeVariables?.strings?.productFormAddBundleWithVase || 'Add Bundle with @vase_name@';
    return template.replace('@vase_name@', vaseName);
  }

  async fetchCart() {
    const cartUrl = `${window.themeVariables?.routes?.cartUrl || '/cart'}.js`;
    const response = await fetch(cartUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  }

  getBranchLineItemProperties(cart) {
    const cartUtils = window.BoaBranchPicker?.cartUtils;
    if (!cartUtils?.buildLineItemProperties || !cart?.attributes?.address_type) {
      return {};
    }

    return cartUtils.buildLineItemProperties(cart.attributes);
  }

  normalizeLineItemProperties(properties) {
    const normalized = {};

    for (const [key, value] of Object.entries(properties || {})) {
      if (value != null && value !== '') {
        normalized[key] = String(value);
      }
    }

    return normalized;
  }

  findExistingBundleLines(cart, flowerVariantId, bundleKey) {
    if (!cart?.items) {
      return { parent: null, child: null };
    }

    const vaseVariantId = Number(bundleKey.split(':').pop());

    const parent = cart.items.find((item) => (
      !item.parent_relationship &&
      item.variant_id === flowerVariantId &&
      item.properties?._bouquet_vase_bundle_key === bundleKey
    ));

    if (!parent) {
      return { parent: null, child: null };
    }

    const child = cart.items.find((item) => {
      if (!item.parent_relationship) {
        return false;
      }

      const parentKey = this.getNestedParentKey(item.parent_relationship);
      return parentKey === parent.key && item.variant_id === vaseVariantId;
    });

    return { parent, child };
  }

  async incrementExistingBundle(parent, child, quantity) {
    const updates = {
      [parent.key]: parent.quantity + quantity
    };

    if (child) {
      updates[child.key] = child.quantity + quantity;
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update bundle quantity: ${errorText}`);
    }

    return response.json();
  }

  async addMissingBundleChild(parent, vaseVariantId, quantity, vaseChildProperties) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{
          id: vaseVariantId,
          quantity,
          properties: vaseChildProperties,
          // Cart AJAX API: parent_line_key links to an existing parent line;
          // parent_id is only for adding parent+child in the same request.
          parent_line_key: parent.key
        }]
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add vase to bundle: ${errorText}`);
    }

    const result = await response.json();
    const addedChild = (result.items || []).find((item) => item.variant_id === vaseVariantId);
    let parentKey = this.getNestedParentKey(addedChild?.parent_relationship);

    if (!parentKey || parentKey !== parent.key) {
      const verifyCart = await this.fetchCart();
      const linkedChild = verifyCart?.items?.find((item) => (
        item.variant_id === vaseVariantId &&
        this.getNestedParentKey(item.parent_relationship) === parent.key
      ));
      if (!linkedChild) {
        throw new Error('Vase was not linked to the bouquet line. Please try again.');
      }
    }

    return result;
  }

  async addNewBundleToCart(bundleSnapshot, bundleKey, flowerVariantId, vaseVariantId, quantity, cart, existingParent = null, existingChild = null) {
    const flowerProperties = this.buildFlowerLineProperties(bundleKey, bundleSnapshot, cart, existingParent);
    const vaseChildProperties = this.buildVaseChildLineProperties(bundleKey, cart, existingChild);

    // Always add parent first, then child via parent_line_key.
    // parent_id attaches to the first matching flower variant — including vase-less lines.
    const parentResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: flowerVariantId, quantity, properties: flowerProperties }]
      })
    });

    if (!parentResponse.ok) {
      const errorText = await parentResponse.text();
      throw new Error(`Failed to add bundle: ${errorText}`);
    }

    const updatedCart = await this.fetchCart();
    const { parent: newParent } = this.findExistingBundleLines(updatedCart, flowerVariantId, bundleKey);

    if (!newParent) {
      throw new Error('Failed to create bundle parent line');
    }

    return this.addMissingBundleChild(newParent, vaseVariantId, quantity, vaseChildProperties);
  }

  buildFlowerLineProperties(bundleKey, bundleSnapshot, cart, existingParent = null) {
    if (existingParent?.properties) {
      return this.normalizeLineItemProperties(existingParent.properties);
    }

    return this.normalizeLineItemProperties({
      _bouquet_vase_bundle_key: bundleKey,
      _bouquet_vase_name: bundleSnapshot.vaseTitle,
      _bouquet_vase_image_url: bundleSnapshot.imageUrl,
      ...this.getBranchLineItemProperties(cart)
    });
  }

  buildVaseChildLineProperties(bundleKey, cart, existingChild = null) {
    if (existingChild?.properties) {
      return this.normalizeLineItemProperties(existingChild.properties);
    }

    return this.normalizeLineItemProperties({
      _bouquet_vase_bundle_key: bundleKey,
      _bouquet_vase_child: 'true',
      ...this.getBranchLineItemProperties(cart)
    });
  }

  async addBundleToCart() {
    if (!this.selectedBundle || this._addInProgress) {
      return;
    }

    const bundleSnapshot = {
      flowerId: this.selectedBundle.flowerId,
      vaseId: this.selectedBundle.vaseId,
      vaseTitle: this.selectedBundle.vaseTitle,
      imageUrl: this.selectedBundle.imageUrl,
      vaseProductTags: this.selectedBundle.vaseProductTags,
      vaseReserveDays: this.selectedBundle.vaseReserveDays,
      vaseVariantTitle: this.selectedBundle.vaseVariantTitle
    };

    const addToCartButtons = this.getAddToCartButtons();
    const quantityInput = this.productForm?.querySelector('input[name="quantity"]');
    
    if (!addToCartButtons.length) {
      this.showCartNotification({
        status: 'error',
        error: 'Add to cart button not found'
      });
      return;
    }

    this._addInProgress = true;
    const setButtonsLoading = (loading) => {
      addToCartButtons.forEach((button) => {
        button.disabled = loading;
        button.classList.toggle('is-loading', loading);
      });
      if (!loading) {
        this._addInProgress = false;
      }
    };

    setButtonsLoading(true);

    try {
      if (typeof getSpecialDateTag === 'function' && typeof checkProductTagMatch === 'function') {
        try {
          const specialDateTag = await getSpecialDateTag();
          if (specialDateTag) {
            const flowerProductTagsJson = this.productForm?.dataset.productTags || '[]';
            let flowerProductTags = [];
            try {
              flowerProductTags = JSON.parse(flowerProductTagsJson);
            } catch (error) {
              flowerProductTags = [];
            }

            const flowerTagMatch = checkProductTagMatch(flowerProductTags, specialDateTag);
            if (!flowerTagMatch) {
              this.showCartNotification({
                status: 'error',
                error: 'This product is not available for the selected delivery date'
              });
              setButtonsLoading(false);
              return;
            }

            const vaseTagMatch = checkProductTagMatch(bundleSnapshot.vaseProductTags || [], specialDateTag);
            if (!vaseTagMatch) {
              this.showCartNotification({
                status: 'error',
                error: 'The selected vase is not available for the selected delivery date'
              });
              setButtonsLoading(false);
              return;
            }
          }
        } catch (error) {
          console.error('Vase bundle special date check failed:', error);
        }
      }

      const quantity = quantityInput ? (parseInt(quantityInput.value) || 1) : 1;
      const bundleKey = `${bundleSnapshot.flowerId}:${bundleSnapshot.vaseId}`;
      const flowerVariantId = Number(bundleSnapshot.flowerId);
      const vaseVariantId = Number(bundleSnapshot.vaseId);
      const cart = await this.fetchCart();
      const { parent: existingParent, child: existingChild } = this.findExistingBundleLines(
        cart,
        flowerVariantId,
        bundleKey
      );
      const vaseChildProperties = this.buildVaseChildLineProperties(bundleKey, cart, existingChild);

      if (existingParent && existingChild) {
        await this.incrementExistingBundle(existingParent, existingChild, quantity);
      } else if (existingParent) {
        const newParentQuantity = existingParent.quantity + quantity;
        await this.incrementExistingBundle(existingParent, null, quantity);
        await this.addMissingBundleChild(
          existingParent,
          vaseVariantId,
          newParentQuantity,
          vaseChildProperties
        );
      } else {
        await this.addNewBundleToCart(
          bundleSnapshot,
          bundleKey,
          flowerVariantId,
          vaseVariantId,
          quantity,
          cart,
          existingParent,
          existingChild
        );
      }

      await this.resetSelection();
      setButtonsLoading(false);
      
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          openMiniCart: true
        }
      }));
    } catch (error) {
      setButtonsLoading(false);
      this.showCartNotification({
        status: 'error',
        error: error.message || 'Failed to add bundle to cart. Please try again.'
      });
    }
  }
}

customElements.define('vase-bundle-carousel', VaseBundleManager);
